"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";

// Edit Date/Time Modal (for Road Shows only)
interface EditDateTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDate: string | null;
  currentStartTime: string | null;
  currentEndTime: string | null;
  onConfirm: (date: string, startTime: string, endTime: string, reason?: string) => Promise<void>;
}

export function EditDateTimeModal({
  isOpen,
  onClose,
  currentDate,
  currentStartTime,
  currentEndTime,
  onConfirm,
}: EditDateTimeModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    currentDate ? new Date(currentDate) : undefined
  );
  const [startTime, setStartTime] = useState(currentStartTime || "09:00");
  const [endTime, setEndTime] = useState(currentEndTime || "17:00");
  const [reason, setReason] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Reset values when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(currentDate ? new Date(currentDate) : undefined);
      setStartTime(currentStartTime || "09:00");
      setEndTime(currentEndTime || "17:00");
      setReason("");
      setIsUpdating(false);
    }
  }, [isOpen, currentDate, currentStartTime, currentEndTime]);

  const handleConfirm = async () => {
    if (!selectedDate) {
      return;
    }

    // Validate time range
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    if (start >= end) {
      return;
    }

    setIsUpdating(true);
    try {
      await onConfirm(
        format(selectedDate, "yyyy-MM-dd"),
        startTime,
        endTime,
        reason.trim() || undefined
      );
      onClose();
    } catch (error) {
      console.error("Error updating date/time:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Date & Time</DialogTitle>
          <DialogDescription>
            Update the date and time for this road show booking
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Date Selection */}
          <div className="grid gap-2">
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="startTime">Start Time</Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="endTime">End Time</Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          {/* Reason Field (Optional) */}
          <div className="grid gap-2">
            <Label htmlFor="reason">
              Reason for Change <span className="text-xs text-muted-foreground">(Optional)</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for date/time change..."
              className="min-h-[60px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedDate || !startTime || !endTime || isUpdating}
          >
            {isUpdating ? "Updating..." : "Update Date & Time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}